import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";

import { generateHreflangLinks, type HreflangLink } from "@/lib/i18n";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "profile" | "product";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
  hreflang?: HreflangLink[];
}

const SITE_NAME = "UGC Lab";
const DEFAULT_TITLE = "UGC Lab - Agencia y Academia de Contenido UGC";
const DEFAULT_DESCRIPTION = "Conectamos marcas con creadores de contenido UGC. Formación, proyectos y oportunidades para el mercado español.";
const DEFAULT_IMAGE = "/opengraph.png";
const SITE_URL = "https://ugclab.es";

const hreflangCache = new Map<string, HTMLLinkElement>();
let hreflangCacheKey = "";

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  section,
  tags,
  noindex = false,
  hreflang,
}: SEOProps) {
  const [location] = useLocation();
  const prevValuesRef = useRef<string>("");
  
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullUrl = url || `${SITE_URL}${location}`;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  
  const hreflangLinks = useMemo(() => hreflang || generateHreflangLinks(location), [hreflang, location]);
  const tagsKey = useMemo(() => tags?.join(",") || "", [tags]);

  useEffect(() => {
    const currentValues = `${fullTitle}|${description}|${fullImage}|${fullUrl}|${type}|${publishedTime}|${modifiedTime}|${author}|${section}|${tagsKey}|${noindex}|${location}`;
    if (prevValuesRef.current === currentValues) {
      return;
    }
    prevValuesRef.current = currentValues;

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      if (meta.getAttribute("content") !== content) {
        meta.setAttribute("content", content);
      }
    };

    const removeMeta = (name: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      const meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta) meta.remove();
    };

    document.title = fullTitle;
    updateMeta("description", description);

    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", description, true);
    updateMeta("og:image", fullImage, true);
    updateMeta("og:url", fullUrl, true);
    updateMeta("og:type", type, true);
    updateMeta("og:site_name", SITE_NAME, true);
    updateMeta("og:locale", "es_ES", true);

    updateMeta("twitter:card", "summary_large_image", true);
    updateMeta("twitter:title", fullTitle, true);
    updateMeta("twitter:description", description, true);
    updateMeta("twitter:image", fullImage, true);

    if (type === "article") {
      if (publishedTime) updateMeta("article:published_time", publishedTime, true);
      if (modifiedTime) updateMeta("article:modified_time", modifiedTime, true);
      if (author) updateMeta("article:author", author, true);
      if (section) updateMeta("article:section", section, true);
      if (tags) {
        tags.forEach((tag, i) => {
          updateMeta(`article:tag:${i}`, tag, true);
        });
      }
    } else {
      removeMeta("article:published_time", true);
      removeMeta("article:modified_time", true);
      removeMeta("article:author", true);
      removeMeta("article:section", true);
    }

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (canonical.getAttribute("href") !== fullUrl) {
      canonical.setAttribute("href", fullUrl);
    }

    if (noindex) {
      updateMeta("robots", "noindex, nofollow");
    } else {
      updateMeta("robots", "index, follow, max-image-preview:large");
    }

    const newCacheKey = hreflangLinks.map(h => `${h.lang}:${h.url}`).join("|");
    if (hreflangCacheKey !== newCacheKey) {
      hreflangCache.forEach(el => el.remove());
      hreflangCache.clear();
      
      hreflangLinks.forEach(({ lang, url: hrefUrl }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", hrefUrl.startsWith("http") ? hrefUrl : `${SITE_URL}${hrefUrl}`);
        document.head.appendChild(link);
        hreflangCache.set(lang, link);
      });
      hreflangCacheKey = newCacheKey;
    }

    return () => {
      document.title = DEFAULT_TITLE;
      hreflangCache.forEach(el => el.remove());
      hreflangCache.clear();
      hreflangCacheKey = "";
    };
  }, [fullTitle, description, fullImage, fullUrl, type, publishedTime, modifiedTime, author, section, tagsKey, noindex, hreflangLinks, location]);

  return null;
}

interface JsonLdProps {
  data: Record<string, unknown>;
  id?: string;
}

let jsonLdCounter = 0;

export function JsonLd({ data, id }: JsonLdProps) {
  useEffect(() => {
    const uniqueId = id || `jsonld-${++jsonLdCounter}-${Date.now()}`;
    const existingScript = document.querySelector(`script[data-jsonld-id="${uniqueId}"]`);
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    script.setAttribute("data-jsonld", "true");
    script.setAttribute("data-jsonld-id", uniqueId);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector(`script[data-jsonld-id="${uniqueId}"]`);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [data, id]);

  return null;
}

export function OrganizationJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
    "@id": `${SITE_URL}/#organization`,
    "name": "UGC Lab",
    "alternateName": "UGC Lab España",
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/logo.png`,
      "width": 512,
      "height": 512
    },
    "image": `${SITE_URL}/og-image.png`,
    "description": "Agencia y Academia de contenido UGC líder en España. Conectamos marcas con creadores profesionales para campañas de contenido auténtico que convierte.",
    "slogan": "Contenido que convierte en Ads y Social",
    "foundingDate": "2023",
    "founder": [
      {
        "@type": "Person",
        "name": "Ivan",
        "jobTitle": "Co-Founder & Tech Lead"
      },
      {
        "@type": "Person", 
        "name": "Uri",
        "jobTitle": "Co-Founder & Creative Director"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Barcelona",
      "addressLocality": "Barcelona",
      "addressRegion": "Cataluña",
      "postalCode": "08001",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 41.3874,
      "longitude": 2.1686
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "España"
      },
      {
        "@type": "Country",
        "name": "Latinoamérica"
      }
    ],
    "serviceType": ["UGC Content Creation", "Creator Management", "UGC Academy", "Brand Partnerships"],
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": ["Credit Card", "Bank Transfer", "PayPal"],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": ["Spanish", "English", "Catalan"],
        "email": "hola@ugclab.es"
      },
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "availableLanguage": ["Spanish", "English"],
        "email": "marcas@ugclab.es"
      }
    ],
    "sameAs": [
      "https://instagram.com/ugclab_es",
      "https://tiktok.com/@ugclab_es",
      "https://linkedin.com/company/ugclab",
      "https://twitter.com/ugclab_es"
    ],
    "knowsAbout": [
      "User Generated Content",
      "UGC Marketing",
      "Content Creation",
      "Social Media Marketing",
      "Influencer Marketing",
      "Video Production",
      "Brand Partnerships"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios UGC Lab",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Creación de Contenido UGC",
            "description": "Videos y fotos UGC para campañas de marketing"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Academia UGC",
            "description": "Formación para creadores de contenido UGC"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Gestión de Creadores",
            "description": "Representación y gestión de talento UGC"
          }
        }
      ]
    }
  };

  return <JsonLd data={data} />;
}

interface CourseJsonLdProps {
  name: string;
  description: string;
  provider?: string;
  price?: string;
  currency?: string;
  url: string;
  image?: string;
  duration?: string;
  instructor?: string;
  courseMode?: "online" | "onsite" | "blended";
  aggregateRating?: { ratingValue: number; reviewCount: number };
  validThrough?: string;
}

export function CourseJsonLd({ 
  name, 
  description, 
  provider = "UGC Lab", 
  price, 
  currency = "EUR", 
  url, 
  image,
  duration,
  instructor,
  courseMode = "online",
  aggregateRating,
  validThrough
}: CourseJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "url": SITE_URL,
      "@id": `${SITE_URL}/#organization`
    },
    "url": url,
    "inLanguage": "es",
    "courseMode": courseMode === "online" ? "online" : courseMode === "onsite" ? "onsite" : "blended",
    "educationalLevel": "Beginner to Advanced",
    "teaches": ["UGC Content Creation", "Video Production", "Brand Collaboration"]
  };

  if (image) data.image = image;
  if (duration) data.timeRequired = duration;
  if (instructor) {
    data.instructor = {
      "@type": "Person",
      "name": instructor,
      "worksFor": {
        "@type": "Organization",
        "name": "UGC Lab"
      }
    };
  }
  if (aggregateRating) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": aggregateRating.ratingValue,
      "reviewCount": aggregateRating.reviewCount,
      "bestRating": 5,
      "worstRating": 1
    };
  }
  if (price) {
    data.offers = {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": "https://schema.org/InStock",
      "category": "Professional Development",
      ...(validThrough && { "validThrough": validThrough })
    };
  }
  data.hasCourseInstance = {
    "@type": "CourseInstance",
    "courseMode": courseMode === "online" ? "online" : courseMode === "onsite" ? "onsite" : "blended",
    "courseWorkload": duration || "P4W"
  };

  return <JsonLd data={data} />;
}

interface EventJsonLdProps {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  locationAddress?: string;
  locationCity?: string;
  locationCountry?: string;
  locationGeo?: { lat: number; lng: number };
  url: string;
  image?: string;
  price?: string;
  currency?: string;
  organizer?: string;
  availability?: "InStock" | "SoldOut" | "PreOrder" | "LimitedAvailability";
  eventStatus?: "EventScheduled" | "EventCancelled" | "EventPostponed" | "EventRescheduled";
  eventAttendanceMode?: "OfflineEventAttendanceMode" | "OnlineEventAttendanceMode" | "MixedEventAttendanceMode";
}

export function EventJsonLd({ 
  name, 
  description, 
  startDate, 
  endDate, 
  location, 
  locationAddress,
  locationCity,
  locationCountry = "ES",
  locationGeo,
  url, 
  image, 
  price, 
  currency = "EUR", 
  organizer = "UGC Lab",
  availability = "InStock",
  eventStatus = "EventScheduled",
  eventAttendanceMode = "OfflineEventAttendanceMode"
}: EventJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": name,
    "description": description,
    "startDate": startDate,
    "url": url,
    "eventStatus": `https://schema.org/${eventStatus}`,
    "eventAttendanceMode": `https://schema.org/${eventAttendanceMode}`,
    "organizer": {
      "@type": "Organization",
      "name": organizer,
      "url": SITE_URL,
      "@id": `${SITE_URL}/#organization`
    },
    "performer": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL
    },
    "inLanguage": "es"
  };

  if (endDate) data.endDate = endDate;
  
  if (location) {
    const placeData: Record<string, unknown> = {
      "@type": "Place",
      "name": location
    };
    
    if (locationAddress || locationCity) {
      placeData.address = {
        "@type": "PostalAddress",
        ...(locationAddress && { "streetAddress": locationAddress }),
        ...(locationCity && { "addressLocality": locationCity }),
        "addressCountry": locationCountry
      };
    }
    
    if (locationGeo) {
      placeData.geo = {
        "@type": "GeoCoordinates",
        "latitude": locationGeo.lat,
        "longitude": locationGeo.lng
      };
    }
    
    data.location = placeData;
  }
  
  if (image) data.image = image;
  
  if (price) {
    data.offers = {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": `https://schema.org/${availability}`,
      "url": url,
      "validFrom": new Date().toISOString()
    };
  }

  return <JsonLd data={data} />;
}

interface PodcastEpisodeJsonLdProps {
  name: string;
  description: string;
  datePublished: string;
  duration?: string;
  url: string;
  audioUrl?: string;
  image?: string;
}

export function PodcastEpisodeJsonLd({ name, description, datePublished, duration, url, audioUrl, image }: PodcastEpisodeJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    "name": name,
    "description": description,
    "datePublished": datePublished,
    "url": url,
    "partOfSeries": {
      "@type": "PodcastSeries",
      "name": "UGC Lab Podcast",
      "url": `${SITE_URL}/podcast`
    }
  };

  if (duration) data.timeRequired = duration;
  if (audioUrl) data.associatedMedia = { "@type": "AudioObject", "contentUrl": audioUrl };
  if (image) data.image = image;

  return <JsonLd data={data} />;
}

interface ArticleJsonLdProps {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  url: string;
}

export function ArticleJsonLd({ headline, description, datePublished, dateModified, author = "UGC Lab", image, url }: ArticleJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "datePublished": datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };

  if (dateModified) data.dateModified = dateModified;
  if (image) data.image = image;

  return <JsonLd data={data} />;
}

interface PersonJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  url: string;
  jobTitle?: string;
  worksFor?: string;
  sameAs?: string[];
}

export function PersonJsonLd({ name, description, image, url, jobTitle, worksFor = "UGC Lab", sameAs }: PersonJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "url": url,
  };

  if (description) data.description = description;
  if (image) data.image = image;
  if (jobTitle) data.jobTitle = jobTitle;
  if (worksFor) {
    data.worksFor = {
      "@type": "Organization",
      "name": worksFor,
      "url": SITE_URL
    };
  }
  if (sameAs && sameAs.length > 0) data.sameAs = sameAs;

  return <JsonLd data={data} />;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQPageJsonLdProps {
  faqs: FAQItem[];
}

export function FAQPageJsonLd({ faqs }: FAQPageJsonLdProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return <JsonLd data={data} />;
}

interface VideoObjectJsonLdProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
  creator?: string;
}

export function VideoObjectJsonLd({ 
  name, 
  description, 
  thumbnailUrl, 
  uploadDate, 
  duration, 
  contentUrl, 
  embedUrl,
  creator = "UGC Lab"
}: VideoObjectJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    "publisher": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "author": {
      "@type": "Person",
      "name": creator
    }
  };

  if (duration) data.duration = duration;
  if (contentUrl) data.contentUrl = contentUrl;
  if (embedUrl) data.embedUrl = embedUrl;

  return <JsonLd data={data} />;
}

interface Review {
  author: string;
  reviewBody: string;
  ratingValue: number;
  datePublished?: string;
}

interface AggregateRatingJsonLdProps {
  itemName: string;
  itemType?: string;
  reviews: Review[];
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}

export function AggregateRatingJsonLd({
  itemName,
  itemType = "Organization",
  reviews,
  ratingValue,
  reviewCount,
  bestRating = 5,
  worstRating = 1
}: AggregateRatingJsonLdProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": itemType,
    "name": itemName,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "reviewCount": reviewCount,
      "bestRating": bestRating,
      "worstRating": worstRating
    },
    "review": reviews.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewBody": review.reviewBody,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.ratingValue,
        "bestRating": bestRating,
        "worstRating": worstRating
      },
      ...(review.datePublished && { "datePublished": review.datePublished })
    }))
  };

  return <JsonLd data={data} />;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbListJsonLdProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbListJsonLd({ items }: BreadcrumbListJsonLdProps) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
    }))
  };

  return <JsonLd data={data} />;
}

interface CreatorProfileJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  url: string;
  niches?: string[];
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  followersCount?: number;
}

export function CreatorProfileJsonLd({
  name,
  description,
  image,
  url,
  niches,
  instagram,
  tiktok,
  youtube,
  followersCount
}: CreatorProfileJsonLdProps) {
  const sameAs: string[] = [];
  if (instagram) sameAs.push(`https://instagram.com/${instagram.replace('@', '')}`);
  if (tiktok) sameAs.push(`https://tiktok.com/@${tiktok.replace('@', '')}`);
  if (youtube) sameAs.push(youtube);

  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "jobTitle": "UGC Creator",
    "worksFor": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL
    }
  };

  if (description) data.description = description;
  if (image) data.image = image;
  if (niches && niches.length > 0) data.knowsAbout = niches;
  if (sameAs.length > 0) data.sameAs = sameAs;
  if (followersCount) {
    data.interactionStatistic = {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/FollowAction",
      "userInteractionCount": followersCount
    };
  }

  return <JsonLd data={data} />;
}

interface PortfolioItemJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  videoUrl?: string;
  creator?: string;
  brand?: string;
  category?: string;
  dateCreated?: string;
  url: string;
}

export function PortfolioItemJsonLd({
  name,
  description,
  image,
  videoUrl,
  creator,
  brand,
  category,
  dateCreated,
  url
}: PortfolioItemJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": name,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "creator": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL
    }
  };

  if (description) data.description = description;
  if (image) data.image = image;
  if (videoUrl) {
    data.video = {
      "@type": "VideoObject",
      "contentUrl": videoUrl,
      "thumbnailUrl": image
    };
  }
  if (creator) {
    data.author = {
      "@type": "Person",
      "name": creator
    };
  }
  if (brand) {
    data.about = {
      "@type": "Brand",
      "name": brand
    };
  }
  if (category) data.genre = category;
  if (dateCreated) data.dateCreated = dateCreated;

  return <JsonLd data={data} />;
}

interface WebPageJsonLdProps {
  name: string;
  description: string;
  url: string;
  breadcrumbs?: BreadcrumbItem[];
}

export function WebPageJsonLd({ name, description, url, breadcrumbs }: WebPageJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "UGC Lab",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "UGC Lab",
      "url": SITE_URL
    }
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    data.breadcrumb = {
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
      }))
    };
  }

  return <JsonLd data={data} />;
}

interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

interface HowToJsonLdProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
  image?: string;
}

export function HowToJsonLd({ name, description, steps, totalTime, image }: HowToJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.image && { "image": step.image })
    }))
  };

  if (totalTime) data.totalTime = totalTime;
  if (image) data.image = image;

  return <JsonLd data={data} id="howto" />;
}

export function WebSiteJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "name": "UGC Lab",
    "alternateName": "UGC Lab España",
    "url": SITE_URL,
    "description": "Agencia y Academia de contenido UGC líder en España",
    "publisher": {
      "@id": `${SITE_URL}/#organization`
    },
    "inLanguage": ["es", "en"],
    "potentialAction": [
      {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/buscar?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    ]
  };

  return <JsonLd data={data} id="website" />;
}

interface ServiceJsonLdProps {
  name: string;
  description: string;
  url: string;
  image?: string;
  provider?: string;
  areaServed?: string;
  priceRange?: string;
  serviceType?: string;
}

export function ServiceJsonLd({
  name,
  description,
  url,
  image,
  provider = "UGC Lab",
  areaServed = "España",
  priceRange,
  serviceType
}: ServiceJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "provider": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": provider
    },
    "areaServed": {
      "@type": "Country",
      "name": areaServed
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": `${SITE_URL}/contacto`,
      "serviceSmsNumber": "+34",
      "serviceLocation": {
        "@type": "Place",
        "name": "Barcelona, España"
      }
    }
  };

  if (image) data.image = image;
  if (priceRange) data.priceRange = priceRange;
  if (serviceType) data.serviceType = serviceType;

  return <JsonLd data={data} id={`service-${name.toLowerCase().replace(/\s+/g, '-')}`} />;
}

export function ServicesJsonLd() {
  const services = [
    {
      name: "Creación de Contenido UGC",
      description: "Videos y fotos UGC profesionales para campañas de marketing. Contenido auténtico que convierte para TikTok, Instagram, Meta Ads y Google Ads.",
      url: "/marcas",
      serviceType: "UGC Content Creation",
      priceRange: "€€-€€€"
    },
    {
      name: "Academia UGC",
      description: "Formación completa para convertirte en creador UGC profesional. Cursos online con certificación, mentoría y acceso a oportunidades con marcas.",
      url: "/academia",
      serviceType: "Online Education",
      priceRange: "€€"
    },
    {
      name: "Gestión de Creadores UGC",
      description: "Representación y gestión de talento UGC. Conectamos creadores con marcas para colaboraciones pagadas y campañas profesionales.",
      url: "/para-creadores",
      serviceType: "Talent Management",
      priceRange: "€€"
    }
  ];

  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Servicios UGC Lab",
    "description": "Servicios profesionales de contenido UGC en España",
    "numberOfItems": services.length,
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.name,
        "description": service.description,
        "url": `${SITE_URL}${service.url}`,
        "serviceType": service.serviceType,
        "priceRange": service.priceRange,
        "provider": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        }
      }
    }))
  };

  return <JsonLd data={data} id="services-list" />;
}

interface VideoObjectEnhancedProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
  creator?: string;
  width?: number;
  height?: number;
  interactionCount?: number;
  genre?: string;
  keywords?: string[];
}

export function VideoObjectEnhancedJsonLd({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration,
  contentUrl,
  embedUrl,
  creator = "UGC Lab",
  width = 1080,
  height = 1920,
  interactionCount,
  genre = "UGC Content",
  keywords
}: VideoObjectEnhancedProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl.startsWith("http") ? thumbnailUrl : `${SITE_URL}${thumbnailUrl}`,
    "uploadDate": uploadDate,
    "publisher": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "author": {
      "@type": "Person",
      "name": creator
    },
    "width": width,
    "height": height,
    "videoQuality": "HD",
    "inLanguage": "es",
    "genre": genre
  };

  if (duration) data.duration = duration;
  if (contentUrl) data.contentUrl = contentUrl;
  if (embedUrl) data.embedUrl = embedUrl;
  if (interactionCount) {
    data.interactionStatistic = {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/WatchAction",
      "userInteractionCount": interactionCount
    };
  }
  if (keywords && keywords.length > 0) data.keywords = keywords.join(", ");

  return <JsonLd data={data} id={`video-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface ProductJsonLdProps {
  name: string;
  description: string;
  image?: string;
  url: string;
  sku?: string;
  brand?: string;
  price: number;
  priceCurrency?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder" | "SoldOut";
  ratingValue?: number;
  reviewCount?: number;
  validFrom?: string;
  validThrough?: string;
  category?: string;
}

export function ProductJsonLd({
  name,
  description,
  image,
  url,
  sku,
  brand = "UGC Lab",
  price,
  priceCurrency = "EUR",
  availability = "InStock",
  ratingValue,
  reviewCount,
  validFrom,
  validThrough,
  category
}: ProductJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "brand": {
      "@type": "Brand",
      "name": brand
    },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": priceCurrency,
      "availability": `https://schema.org/${availability}`,
      "seller": {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": "UGC Lab"
      },
      ...(validFrom && { "validFrom": validFrom }),
      ...(validThrough && { "priceValidUntil": validThrough })
    }
  };

  if (image) data.image = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  if (sku) data.sku = sku;
  if (category) data.category = category;
  
  if (ratingValue && reviewCount) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "reviewCount": reviewCount,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  return <JsonLd data={data} id={`product-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface CourseProductJsonLdProps {
  name: string;
  description: string;
  image?: string;
  url: string;
  price: number;
  originalPrice?: number;
  tier: "BASIC" | "PRO";
  duration?: string;
  instructor?: string;
  ratingValue?: number;
  reviewCount?: number;
  enrollmentCount?: number;
}

export function CourseProductJsonLd({
  name,
  description,
  image,
  url,
  price,
  originalPrice,
  tier,
  duration,
  instructor = "UGC Lab Academy",
  ratingValue,
  reviewCount,
  enrollmentCount
}: CourseProductJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": ["Product", "Course"],
    "name": name,
    "description": description,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "provider": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab Academy"
    },
    "brand": {
      "@type": "Brand",
      "name": "UGC Lab Academy"
    },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "category": tier === "PRO" ? "Premium Course" : "Basic Course",
      "seller": {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`
      }
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": duration || "PT20H",
      "instructor": {
        "@type": "Person",
        "name": instructor
      }
    },
    "educationalLevel": tier === "PRO" ? "Advanced" : "Beginner",
    "inLanguage": "es"
  };

  if (image) data.image = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  
  if (originalPrice && originalPrice > price) {
    (data.offers as Record<string, unknown>).priceSpecification = {
      "@type": "UnitPriceSpecification",
      "price": price,
      "priceCurrency": "EUR",
      "referenceQuantity": {
        "@type": "QuantitativeValue",
        "value": 1
      }
    };
  }

  if (ratingValue && reviewCount) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "reviewCount": reviewCount,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  if (enrollmentCount) {
    data.interactionStatistic = {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/EnrollAction",
      "userInteractionCount": enrollmentCount
    };
  }

  return <JsonLd data={data} id={`course-product-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface ReviewJsonLdProps {
  itemReviewed: {
    type: "Organization" | "Product" | "Course" | "Service";
    name: string;
    url?: string;
  };
  author: string;
  authorType?: "Person" | "Organization";
  reviewBody: string;
  ratingValue: number;
  datePublished?: string;
  reviewRating?: {
    bestRating?: number;
    worstRating?: number;
  };
}

export function ReviewJsonLd({
  itemReviewed,
  author,
  authorType = "Person",
  reviewBody,
  ratingValue,
  datePublished,
  reviewRating = { bestRating: 5, worstRating: 1 }
}: ReviewJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": itemReviewed.type,
      "name": itemReviewed.name,
      ...(itemReviewed.url && { "url": itemReviewed.url.startsWith("http") ? itemReviewed.url : `${SITE_URL}${itemReviewed.url}` })
    },
    "author": {
      "@type": authorType,
      "name": author
    },
    "reviewBody": reviewBody,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": ratingValue,
      "bestRating": reviewRating.bestRating,
      "worstRating": reviewRating.worstRating
    }
  };

  if (datePublished) data.datePublished = datePublished;

  return <JsonLd data={data} id={`review-${author.toLowerCase().replace(/\s+/g, '-').slice(0, 15)}-${ratingValue}`} />;
}

interface ReviewsListJsonLdProps {
  reviews: Array<{
    author: string;
    reviewBody: string;
    ratingValue: number;
    datePublished?: string;
  }>;
  itemReviewed: {
    type: "Organization" | "Product" | "Course" | "Service";
    name: string;
    url?: string;
  };
}

export function ReviewsListJsonLd({ reviews, itemReviewed }: ReviewsListJsonLdProps) {
  const avgRating = reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + r.ratingValue, 0) / reviews.length 
    : 0;

  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Opiniones sobre ${itemReviewed.name}`,
    "numberOfItems": reviews.length,
    "itemListElement": reviews.slice(0, 10).map((review, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Review",
        "itemReviewed": {
          "@type": itemReviewed.type,
          "name": itemReviewed.name,
          ...(itemReviewed.url && { "url": itemReviewed.url.startsWith("http") ? itemReviewed.url : `${SITE_URL}${itemReviewed.url}` })
        },
        "author": {
          "@type": "Person",
          "name": review.author
        },
        "reviewBody": review.reviewBody,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.ratingValue,
          "bestRating": 5,
          "worstRating": 1
        },
        ...(review.datePublished && { "datePublished": review.datePublished })
      }
    })),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": avgRating.toFixed(1),
      "reviewCount": reviews.length,
      "bestRating": 5,
      "worstRating": 1
    }
  };

  return <JsonLd data={data} id="reviews-list" />;
}

interface ProfilePageJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  url: string;
  sameAs?: string[];
  jobTitle?: string;
  worksFor?: string;
  skills?: string[];
  knowsAbout?: string[];
}

export function ProfilePageJsonLd({
  name,
  description,
  image,
  url,
  sameAs,
  jobTitle = "Creador UGC",
  worksFor = "UGC Lab",
  skills,
  knowsAbout
}: ProfilePageJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": name,
      "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
      "jobTitle": jobTitle,
      "worksFor": {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": worksFor
      }
    }
  };

  const mainEntity = data.mainEntity as Record<string, unknown>;
  
  if (description) mainEntity.description = description;
  if (image) mainEntity.image = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  if (sameAs && sameAs.length > 0) mainEntity.sameAs = sameAs;
  if (skills && skills.length > 0) mainEntity.hasOccupation = {
    "@type": "Occupation",
    "name": jobTitle,
    "skills": skills.join(", ")
  };
  if (knowsAbout && knowsAbout.length > 0) mainEntity.knowsAbout = knowsAbout;

  return <JsonLd data={data} id={`profile-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

export function ContactPageJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contacto - UGC Lab",
    "description": "Página de contacto de UGC Lab. Ponte en contacto con nosotros para proyectos UGC, formación o colaboraciones.",
    "url": `${SITE_URL}/contacto`,
    "mainEntity": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": ["Spanish", "English"],
          "email": "hola@ugclab.es"
        },
        {
          "@type": "ContactPoint",
          "contactType": "sales",
          "availableLanguage": ["Spanish", "English"],
          "email": "marcas@ugclab.es"
        }
      ]
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contacto",
          "item": `${SITE_URL}/contacto`
        }
      ]
    }
  };

  return <JsonLd data={data} id="contact-page" />;
}

export function AboutPageJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Sobre Nosotros - UGC Lab",
    "description": "Conoce al equipo detrás de UGC Lab, la agencia y academia de contenido UGC líder en España.",
    "url": `${SITE_URL}/sobre-nosotros`,
    "mainEntity": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab",
      "description": "Agencia y Academia de contenido UGC en España. Conectamos marcas con creadores de contenido auténtico.",
      "foundingDate": "2023",
      "foundingLocation": {
        "@type": "Place",
        "name": "Barcelona, España"
      },
      "knowsAbout": [
        "User Generated Content",
        "Marketing Digital",
        "Creación de Contenido",
        "Redes Sociales",
        "TikTok Marketing",
        "Instagram Marketing",
        "Formación UGC"
      ],
      "slogan": "Contenido auténtico que convierte"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Sobre Nosotros",
          "item": `${SITE_URL}/sobre-nosotros`
        }
      ]
    }
  };

  return <JsonLd data={data} id="about-page" />;
}

interface JobPostingJsonLdProps {
  title: string;
  description: string;
  datePosted: string;
  validThrough?: string;
  employmentType?: "FULL_TIME" | "PART_TIME" | "CONTRACTOR" | "FREELANCE" | "INTERN";
  jobLocationType?: "TELECOMMUTE" | "ONSITE" | "HYBRID";
  baseSalary?: {
    minValue?: number;
    maxValue?: number;
    currency?: string;
    unitText?: "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR";
  };
  skills?: string[];
  qualifications?: string[];
  responsibilities?: string[];
  url: string;
  applicationDeadline?: string;
  hiringOrganization?: string;
  jobBenefits?: string[];
  experienceRequirements?: string;
  educationRequirements?: string;
}

export function JobPostingJsonLd({
  title,
  description,
  datePosted,
  validThrough,
  employmentType = "FREELANCE",
  jobLocationType = "TELECOMMUTE",
  baseSalary,
  skills,
  qualifications,
  responsibilities,
  url,
  applicationDeadline,
  hiringOrganization = "UGC Lab",
  jobBenefits,
  experienceRequirements,
  educationRequirements
}: JobPostingJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": title,
    "description": description,
    "datePosted": datePosted,
    "hiringOrganization": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": hiringOrganization,
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo.png`
    },
    "employmentType": employmentType,
    "jobLocationType": jobLocationType,
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "España"
    },
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "industry": "Marketing y Publicidad",
    "occupationalCategory": "27-3091.00"
  };

  if (validThrough) data.validThrough = validThrough;
  if (applicationDeadline) data.applicationDeadline = applicationDeadline;
  
  if (baseSalary) {
    data.baseSalary = {
      "@type": "MonetaryAmount",
      "currency": baseSalary.currency || "EUR",
      "value": {
        "@type": "QuantitativeValue",
        ...(baseSalary.minValue && { "minValue": baseSalary.minValue }),
        ...(baseSalary.maxValue && { "maxValue": baseSalary.maxValue }),
        "unitText": baseSalary.unitText || "MONTH"
      }
    };
  }

  if (skills && skills.length > 0) data.skills = skills.join(", ");
  if (qualifications && qualifications.length > 0) data.qualifications = qualifications.join(", ");
  if (responsibilities && responsibilities.length > 0) data.responsibilities = responsibilities.join(", ");
  if (jobBenefits && jobBenefits.length > 0) data.jobBenefits = jobBenefits.join(", ");
  if (experienceRequirements) data.experienceRequirements = experienceRequirements;
  if (educationRequirements) data.educationRequirements = educationRequirements;

  return <JsonLd data={data} id={`job-${title.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface CreatorOpportunityJsonLdProps {
  title: string;
  description: string;
  brand?: string;
  compensation?: string;
  datePosted: string;
  validThrough?: string;
  niches?: string[];
  requirements?: string[];
  url: string;
}

export function CreatorOpportunityJsonLd({
  title,
  description,
  brand,
  compensation,
  datePosted,
  validThrough,
  niches,
  requirements,
  url
}: CreatorOpportunityJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": title,
    "description": description,
    "datePosted": datePosted,
    "hiringOrganization": {
      "@type": "Organization",
      "name": brand || "Marca colaboradora de UGC Lab",
      "url": SITE_URL
    },
    "employmentType": "CONTRACTOR",
    "jobLocationType": "TELECOMMUTE",
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "España"
    },
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "industry": "Creación de Contenido UGC",
    "directApply": true
  };

  if (validThrough) data.validThrough = validThrough;
  if (compensation) {
    data.baseSalary = {
      "@type": "MonetaryAmount",
      "currency": "EUR",
      "value": {
        "@type": "QuantitativeValue",
        "value": compensation,
        "unitText": "PROJECT"
      }
    };
  }
  if (niches && niches.length > 0) data.skills = niches.join(", ");
  if (requirements && requirements.length > 0) data.qualifications = requirements.join(", ");

  return <JsonLd data={data} id={`opportunity-${title.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface SoftwareApplicationJsonLdProps {
  name: string;
  description: string;
  applicationCategory?: string;
  operatingSystem?: string;
  url: string;
  screenshot?: string;
  aggregateRating?: {
    ratingValue: number;
    ratingCount: number;
  };
  offers?: {
    price: number;
    priceCurrency?: string;
  };
}

export function SoftwareApplicationJsonLd({
  name,
  description,
  applicationCategory = "BusinessApplication",
  operatingSystem = "Web",
  url,
  screenshot,
  aggregateRating,
  offers
}: SoftwareApplicationJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "applicationCategory": applicationCategory,
    "operatingSystem": operatingSystem,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "provider": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab"
    }
  };

  if (screenshot) data.screenshot = screenshot.startsWith("http") ? screenshot : `${SITE_URL}${screenshot}`;
  
  if (aggregateRating) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": aggregateRating.ratingValue,
      "ratingCount": aggregateRating.ratingCount,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  if (offers) {
    data.offers = {
      "@type": "Offer",
      "price": offers.price,
      "priceCurrency": offers.priceCurrency || "EUR"
    };
  }

  return <JsonLd data={data} id="software-app" />;
}

interface CollectionPageJsonLdProps {
  name: string;
  description: string;
  url: string;
  numberOfItems?: number;
  itemType?: string;
}

export function CollectionPageJsonLd({
  name,
  description,
  url,
  numberOfItems,
  itemType = "CreativeWork"
}: CollectionPageJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": name,
    "description": description,
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "name": "UGC Lab"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": {
        "@type": itemType
      }
    }
  };

  if (numberOfItems) {
    (data.mainEntity as Record<string, unknown>).numberOfItems = numberOfItems;
  }

  return <JsonLd data={data} id={`collection-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`} />;
}

interface SpecialAnnouncementJsonLdProps {
  name: string;
  text: string;
  datePosted: string;
  expires?: string;
  category?: string;
  url: string;
  locationCreated?: string;
}

export function SpecialAnnouncementJsonLd({
  name,
  text,
  datePosted,
  expires,
  category = "announcement",
  url,
  locationCreated = "Barcelona, España"
}: SpecialAnnouncementJsonLdProps) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SpecialAnnouncement",
    "name": name,
    "text": text,
    "datePosted": datePosted,
    "category": `https://www.wikidata.org/wiki/Q${category === "announcement" ? "5389107" : "5389107"}`,
    "announcementLocation": {
      "@type": "Place",
      "name": locationCreated
    },
    "url": url.startsWith("http") ? url : `${SITE_URL}${url}`,
    "publisher": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "UGC Lab"
    }
  };

  if (expires) data.expires = expires;

  return <JsonLd data={data} id="special-announcement" />;
}
